package com.anjuros.website.redesign.sections.designs

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.redesign.components.RdBreadcrumb
import com.anjuros.website.redesign.components.RdGhostButton
import com.anjuros.website.redesign.components.RdLede
import com.anjuros.website.redesign.components.RdPicture
import com.anjuros.website.redesign.components.RdPrimaryButton
import com.anjuros.website.redesign.components.RdSection
import com.anjuros.website.redesign.styles.DesignsEqButtonStyle
import com.anjuros.website.redesign.styles.DesignsEqCardStyle
import com.anjuros.website.redesign.styles.DesignsEqChipsStyle
import com.anjuros.website.redesign.styles.DesignsEqLabelStyle
import com.anjuros.website.redesign.styles.DesignsEqLineShortStyle
import com.anjuros.website.redesign.styles.DesignsEqLineStyle
import com.anjuros.website.redesign.styles.DesignsEqOperatorStyle
import com.anjuros.website.redesign.styles.DesignsEqResultCardStyle
import com.anjuros.website.redesign.styles.DesignsEqSiteBarStyle
import com.anjuros.website.redesign.styles.DesignsEqSiteBodyStyle
import com.anjuros.website.redesign.styles.DesignsEqSiteStyle
import com.anjuros.website.redesign.styles.DesignsEqThumbsStyle
import com.anjuros.website.redesign.styles.DesignsEqWordStyle
import com.anjuros.website.redesign.styles.DesignsEquationStyle
import com.anjuros.website.redesign.styles.RdButtonRowStyle
import com.anjuros.website.redesign.styles.RdHeroCopyStyle
import com.anjuros.website.redesign.styles.RdHeroGridStyle
import com.anjuros.website.redesign.styles.RdHeroStyle
import com.anjuros.website.redesign.styles.RdMarkStyle
import com.anjuros.website.redesign.styles.RdSubpageTitleStyle
import com.anjuros.website.redesign.util.RdImage
import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText
import com.anjuros.website.redesign.util.rdList
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

private val EquationThumbs = listOf(
    RdImage.DESIGN_ELECTRICIAN,
    RdImage.DESIGN_DECORATOR,
    RdImage.DESIGN_DOG_WALKER,
    RdImage.DESIGN_PUB,
)

@Composable
fun DesignsHeroSection() {
    RdSection(labelledBy = "h1", modifier = RdHeroStyle.toModifier(), wrapModifier = RdHeroGridStyle.toModifier()) {
        Div(RdHeroCopyStyle.toModifier().toAttrs()) {
            RdBreadcrumb(RdText.DESIGNS_CRUMB)
            H1(RdSubpageTitleStyle.toModifier().id("h1").toAttrs()) {
                Text(LocalText(RdText.DESIGNS_HERO_TITLE_START))
                Span(RdMarkStyle.toModifier().toAttrs()) { Text(LocalText(RdText.DESIGNS_HERO_TITLE_MARK)) }
            }
            RdLede(RdText.DESIGNS_HERO_LEDE)
            Div(RdButtonRowStyle.toModifier().toAttrs()) {
                RdPrimaryButton("#catalogue", RdText.DESIGNS_HERO_BROWSE, withArrow = true)
                RdGhostButton(RdRoute.CONTACT, RdText.CTA_FREE_PREVIEW)
            }
        }
        Equation()
    }
}

/** Any design + your business = your website: shows that every design fits every business. */
@Composable
private fun Equation() {
    val label = LocalText(RdText.DESIGNS_EQ_LABEL)
    Div(DesignsEquationStyle.toModifier().attrsModifier { attr("aria-label", label); attr("role", "img") }.toAttrs()) {
        Div(DesignsEqCardStyle.toModifier().toAttrs()) {
            EquationLabel(RdText.DESIGNS_EQ_ANY)
            Div(DesignsEqThumbsStyle.toModifier().toAttrs()) {
                EquationThumbs.forEach { RdPicture(it, alt = "", lazy = false) }
            }
        }
        Operator("+")
        Div(DesignsEqCardStyle.toModifier().toAttrs()) {
            EquationLabel(RdText.DESIGNS_EQ_YOURS)
            Ul(DesignsEqChipsStyle.toModifier().toAttrs()) {
                rdList(LocalText(RdText.DESIGNS_EQ_CHIPS)).forEach { Li { Text(it) } }
            }
        }
        Operator("=")
        Div(DesignsEqCardStyle.toModifier().then(DesignsEqResultCardStyle.toModifier()).toAttrs()) {
            EquationLabel(RdText.DESIGNS_EQ_RESULT)
            WebsiteSketch()
        }
    }
}

@Composable
private fun EquationLabel(text: RdText) {
    P(DesignsEqLabelStyle.toModifier().toAttrs()) { Text(LocalText(text)) }
}

@Composable
private fun Operator(symbol: String) {
    Span(DesignsEqOperatorStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) { Text(symbol) }
}

/** A tiny website whose business type cycles through examples. */
@Composable
private fun WebsiteSketch() {
    val words = rdList(LocalText(RdText.DESIGNS_EQ_WORDS))
    var index by remember { mutableStateOf(0) }

    LaunchedEffect(words) {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return@LaunchedEffect
        while (true) {
            delay(2000)
            index = (index + 1) % words.size
        }
    }

    Div(DesignsEqSiteStyle.toModifier().attrsModifier { attr("aria-hidden", "true") }.toAttrs()) {
        Div(DesignsEqSiteBarStyle.toModifier().toAttrs()) { repeat(3) { Span() } }
        Div(DesignsEqSiteBodyStyle.toModifier().toAttrs()) {
            B {
                Text(LocalText(RdText.DESIGNS_EQ_SITE_START))
                Span(DesignsEqWordStyle.toModifier().toAttrs()) { Text(words[index]) }
                Text(LocalText(RdText.DESIGNS_EQ_SITE_END))
            }
            Span(DesignsEqLineStyle.toModifier().toAttrs())
            Span(DesignsEqLineStyle.toModifier().then(DesignsEqLineShortStyle.toModifier()).toAttrs())
            Span(DesignsEqButtonStyle.toModifier().toAttrs()) { Text(LocalText(RdText.DESIGNS_EQ_BUTTON)) }
        }
    }
}
